import type { StructuredContentDocument } from './types';

export const isStructuredContent = (section: unknown): section is StructuredContentDocument => {
  return (
    section !== null &&
    typeof section !== 'undefined' &&
    typeof section === 'object' &&
    'type' in section &&
    'version' in section &&
    'props' in section
  );
};

/**
 * Takes a node's `children` and returns the nodes inside it, always as an array.
 */
export const toStructuredContentNodes = (children: unknown): StructuredContentDocument[] => {
  return [children].flat().filter(isStructuredContent);
};

export const isStructuredContentType = <T = Record<string, unknown>>(
  value: unknown,
  type: string,
): value is StructuredContentDocument<T> => {
  return isStructuredContent(value) && value.type === type;
};

/**
 * The nodes in `children` that are of the given type.
 *
 * Anything else is dropped, and missing `children` gives an empty array.
 */
export const filterStructuredContentByType = <T = Record<string, unknown>>({
  children,
  type,
}: {
  children?: StructuredContentDocument<T>[];
  type: string;
}): StructuredContentDocument<T>[] => {
  return [children]
    .flat()
    .filter((node): node is StructuredContentDocument<T> => isStructuredContentType<T>(node, type));
};

export const findStructuredContentByType = <T = Record<string, unknown>>(
  node: unknown,
  type: string,
): StructuredContentDocument<T> | null => {
  if (Array.isArray(node)) {
    for (const child of node) {
      const found = findStructuredContentByType<T>(child, type);
      if (found) {
        return found;
      }
    }
    return null;
  }

  if (!isStructuredContent(node)) {
    return null;
  }

  if (isStructuredContentType<T>(node, type)) {
    return node;
  }

  return findStructuredContentByType<T>(node.props.children, type);
};

export const getAllChildrenStrings = (
  sc: StructuredContentDocument<Record<string, unknown>>,
  prevValue = '',
): string => {
  if (Array.isArray(sc.props.children)) {
    for (const child of sc.props.children) {
      if (typeof child === 'string') {
        prevValue = prevValue.concat(child);
      }
      if (isStructuredContent(child)) {
        prevValue = getAllChildrenStrings(child, prevValue);
      }
    }
  }
  if (isStructuredContent(sc?.props?.children)) {
    prevValue = getAllChildrenStrings(sc?.props?.children, prevValue);
  }
  return prevValue;
};
